/**
 * useVendorImportSocketSync: keeps what the signed-in vendor's imports
 * through the vendor API make current on every page. Mounted in the header
 * beside useReceiptUploadSocketSync, which it copies, so a push reaches the
 * cache wherever the vendor is (docs/plans/vendor-api.md, The import job 5).
 *
 * The import's job pushes how an import stands every few entries and when it
 * finishes (VENDOR_IMPORT_SOCKET_EVENTS.IMPORT_CHANGED), never on a timer:
 * - the banners' list writes the push into itself, in its own
 *   onCacheEntryAdded (vendorApi, getVendorImports), so the count made moves
 *   with no request;
 * - here, each push means items or listings were made, so the inventory and
 *   listings are marked stale, and the Listings page shows the drafts as the
 *   import makes them.
 *
 * A socket that dropped may have missed a push, so on Socket.IO's own
 * "connect", which fires on every reconnect, the list and the inventory and
 * listings are marked stale. A push that fails its schema is logged here
 * and never half applied: the list reads itself again, and what an import
 * makes is marked stale anyway. The socket comes from context, as the
 * header's own listeners take it; the effect runs again when it appears.
 */
import { useEffect } from 'react'
import { VENDOR_IMPORT_SOCKET_EVENTS, VendorImportChangedSchema } from '@gearghost/api-schemas/vendorApi'
import { invalidateInventoryAndListings } from '@/redux/api/receiptsApi'
import { vendorApi } from '@/redux/api/vendorApi'
import { useAppDispatch } from '@/redux/hooks'
import { SOCKET_CONNECT, useSocket } from '@/redux/socketContext'
import { createLogger } from '@/utils/logger'

const log = createLogger('useVendorImportSocketSync')

export const useVendorImportSocketSync = () => {
  const { socket } = useSocket()
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!socket) return
    const changed = (message: { payload: unknown }) => {
      const parsed = VendorImportChangedSchema.safeParse(message.payload)
      if (!parsed.success) {
        log.error({ issues: parsed.error.issues }, 'an import-changed push did not carry its import; the imports and what they make are read again')
      }
      invalidateInventoryAndListings(dispatch)
    }
    const reconnected = () => {
      dispatch(vendorApi.util.invalidateTags(['VendorImports']))
      invalidateInventoryAndListings(dispatch)
    }
    socket.on(VENDOR_IMPORT_SOCKET_EVENTS.IMPORT_CHANGED, changed)
    socket.on(SOCKET_CONNECT, reconnected)
    return () => {
      socket.off(VENDOR_IMPORT_SOCKET_EVENTS.IMPORT_CHANGED, changed)
      socket.off(SOCKET_CONNECT, reconnected)
    }
  }, [socket, dispatch])
}
